import React, { useEffect, useMemo } from "react";

import Grid from "@material-ui/core/Grid";
import { addDays } from "date-fns";
import { useFormikContext } from "formik";
import isNumber from "lodash/isNumber";
import { useDispatch, useSelector } from "react-redux";

import { BillingRunAction } from "actions";

import { Warning } from "components/ErrorMessage";
import { BusinessField } from "components/Form/Fields";
import { BankingDetailField } from "components/Form/Fields/BankingDetailField";
import { HTMLTemplateTextField } from "components/Form/Fields/DetailsText";
import {
  DateInput,
  Input,
  SelectField,
  withNamespace,
} from "components/Form/FormikControls";

import { BillingNumberPrefixes } from "constants/billing";
import {
  BillingDocumentStatus,
  BillingDocumentStatusOptions,
  BillingDocumentType,
} from "constants/billingDocuments";
import { BillingRunStatus } from "constants/billingRuns";
import { SaleType } from "constants/sale";

import {
  formatDateString,
  getDateFromISO8601DateString,
} from "lib/timeFormats";

import {
  getBillingSettingsByBillingRunId,
  getBillingDocumentById,
  getBillingRunById,
  getCurrentSale,
  getSales,
  selectBusinessTradingTermsByIdLookup,
} from "selectors";

interface BusinessTradingTerm {
  saleSubTypeId: number | null;
  saleTypeId?: string | null;
  speciesId: number | null;
}

interface SaleContext {
  saleSubTypeId?: number | null;
  speciesId?: number | null;
  saleTypeId?: string | null;
}

function selectBestTradingTerm(
  terms: BusinessTradingTerm[],
  livestockSale: SaleContext,
): BusinessTradingTerm | null {
  const { saleSubTypeId, speciesId, saleTypeId } = livestockSale;

  let termForSaleTypeAndSpecies: BusinessTradingTerm | null = null;
  let termForSaleType: BusinessTradingTerm | null = null;
  let termForFallback: BusinessTradingTerm | null = null;

  for (const term of terms) {
    if (term.saleSubTypeId && term.saleSubTypeId === saleSubTypeId) {
      // Return immediately if the most specific term is found
      return term;
    }

    if (term.saleTypeId === saleTypeId) {
      if (term.speciesId === speciesId) {
        termForSaleTypeAndSpecies = term;
      } else {
        termForSaleType = term;
      }
    }

    if (!term.saleSubTypeId && !term.speciesId && !term.saleTypeId) {
      termForFallback = term;
    }
  }

  // Return the most specific match, or fallback to the least specific
  return termForSaleTypeAndSpecies || termForSaleType || termForFallback;
}

interface BillingDocumentFormProps {
  billingDocumentId: string;
  saleId?: string | null;
  namespace?: string;
  prefillApplied?: boolean;
}

interface BillingDocumentFormValues {
  status: string;
  dueDate: string | null;
  number: string | number | null;
}

export function BillingDocumentForm(props: BillingDocumentFormProps) {
  const {
    billingDocumentId,
    saleId = null,
    namespace: ns = "",
    prefillApplied = false,
  } = props;

  let sale = useSelector(getCurrentSale);
  const sales = useSelector(getSales);
  if (saleId) {
    sale = sales[saleId];
  }
  const saleDate = sale.date;

  const billingDocument =
    (useSelector(
      getBillingDocumentById(billingDocumentId),
    ) as Partial<BillingDocument>) || {};
  const {
    type: documentType,
    billingRunId,
    issuerBusinessId,
    recipientBusinessId,
  } = billingDocument;

  const billingSettings = useSelector(
    getBillingSettingsByBillingRunId(billingRunId),
  ) as BillingSettings | null;

  const businessTradingTerms = useSelector(
    selectBusinessTradingTermsByIdLookup,
  ) as Record<string, BusinessTradingTerm[]>;

  const deploymentSale = sale?.deployment_sales?.[0] || {};
  const defaultBuyerTerms = deploymentSale.buyer_terms || 0;
  const defaultVendorTerms = deploymentSale.vendor_terms || 0;

  const issuerBusinessTerms = useMemo(
    () =>
      selectBestTradingTerm(
        businessTradingTerms?.[issuerBusinessId] || [],
        sale as SaleContext,
      ),
    [businessTradingTerms, issuerBusinessId, sale],
  );

  const recipientBusinessTerms = useMemo(
    () =>
      selectBestTradingTerm(
        businessTradingTerms?.[recipientBusinessId] || [],
        sale as SaleContext,
      ),
    [businessTradingTerms, recipientBusinessId, sale],
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (billingRunId) {
      dispatch(BillingRunAction.requestOne({ id: billingRunId }));
    }
  }, [billingRunId, dispatch]);

  const billingRun = useSelector(
    getBillingRunById(billingRunId),
  ) as BillingRun | null;

  const {
    values: { status: currentStatus, dueDate, number },
  } = useFormikContext<BillingDocumentFormValues>();

  // Show a warning based on trading terms and due date.
  const hasRelevantTradingTerms = !!(
    recipientBusinessTerms || issuerBusinessTerms
  );
  const terms =
    (documentType === BillingDocumentType.RCTI
      ? defaultVendorTerms
      : defaultBuyerTerms) || 0;

  // Convert to a date, add the terms, then back to a string for comparison.
  const expectedDueDate = formatDateString(
    addDays(getDateFromISO8601DateString(saleDate), terms),
  );
  const dueDateIsDifferent =
    dueDate &&
    expectedDueDate !== formatDateString(getDateFromISO8601DateString(dueDate));

  const prefix = useMemo(() => {
    // If number is not a number, assume it already has a prefix.
    if (number && !isNumber(number)) {
      return "";
    }

    if (documentType === BillingDocumentType.RCTI) {
      return billingSettings?.accountSalePrefix || BillingNumberPrefixes.RCTI;
    } else {
      return billingSettings?.invoicePrefix || BillingNumberPrefixes.Invoice;
    }
  }, [
    number,
    documentType,
    billingSettings?.accountSalePrefix,
    billingSettings?.invoicePrefix,
  ]);

  const referenceSale = sale.sale_type === SaleType.REFERENCE_SALE;
  const billingRunFinished = billingRun?.status === BillingRunStatus.FINISHED;
  const multipleBankingDetails =
    billingSettings.paymentBankingDetails?.length > 1;

  const disabled = useMemo(() => {
    return referenceSale || billingRunFinished;
  }, [billingRunFinished, referenceSale]);

  return (
    <Grid container spacing={2}>
      {referenceSale && (
        <Grid item xs={12}>
          <Warning>
            Information record only, this has been invoiced externally.
          </Warning>
        </Grid>
      )}
      {billingRunFinished && (
        <Grid item xs={12}>
          <Warning>
            Information record only, this documents billing run is finished,
          </Warning>
        </Grid>
      )}
      <Grid item xs={6}>
        <SelectField
          label="Status"
          name={withNamespace(ns, "status")}
          options={BillingDocumentStatusOptions}
          required
          menuPortalTarget={document.body}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={6}>
        <Input
          beforeSymbol={prefix}
          label="Document Number"
          name={withNamespace(ns, "number")}
          tooltip="The reference number assigned to this document.  This will be automatically populated when a document is approved."
          placeholder="Assigned on Approval"
          emptyValue={null}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={6}>
        <BusinessField name="recipientBusinessId" label="Recipient" disabled />
      </Grid>
      <Grid item xs={6}>
        <BusinessField name="issuerBusinessId" label="Issuer" disabled />
      </Grid>

      <Grid item xs={12}>
        <DateInput
          name="dueDate"
          label="Due Date"
          min="2010-01-01"
          required
          disabled={currentStatus !== BillingDocumentStatus.DRAFT || disabled}
        />
        {!disabled && hasRelevantTradingTerms ? (
          <Warning>
            This date is driven by the businesses trading terms.
          </Warning>
        ) : !disabled && dueDateIsDifferent ? (
          <Warning>
            Warning: The date entered is different to the default (
            {expectedDueDate}) for this document type.
          </Warning>
        ) : null}
      </Grid>
      <Grid item xs={12}>
        <DateInput
          name="plannedPaymentDate"
          label="Planned Payment Date"
          min="2010-01-01"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          label="Planned Payment Note"
          name="plannedPaymentNote"
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} md={12}>
        <HTMLTemplateTextField name="detailsText" disabled={disabled} />
        {!disabled && prefillApplied ? (
          <Warning>
            The Details Text has been prefilled from your Agency Settings.
          </Warning>
        ) : null}
      </Grid>
      <Grid item xs={12}>
        <Input
          label="External Ref/s"
          name="billingReference"
          maxLength={255}
          disabled={disabled}
        />
      </Grid>
      {multipleBankingDetails &&
        documentType === BillingDocumentType.INVOICE && (
          <Grid item xs={12}>
            <BankingDetailField
              name="paymentBankingDetailsId"
              namespace={ns}
              label="Bank Account for Payments"
              placeholder="Use default"
              tooltip="Allows overriding which Bank Account is shown on an Invoice for receiving Payments."
              disabled={disabled}
            />
          </Grid>
        )}
    </Grid>
  );
}
